<template>
  <div ref="imagesContainerRef" class="images-wrapper">
    <View align="center" justify="center" class="file">
      <FileViewerImage v-if="isImage(activeFile.mimeType)" :src="activeFile.url" />
      <FileViewerPdf v-else-if="isPdf(activeFile.mimeType)" :src="activeFile.url" />

      <View v-else gap="md" direction="column" align="center">
        <Icon name="file-x-03" size="large" />
        <Text variant="body-large">File is not supported for preview</Text>
        <Link :to="activeFile.url" target="_blank" color="accent">Download</Link>
      </View>
    </View>

    <IconButton
      v-if="activeIndex !== 0"
      class="prev-button"
      variant="secondary"
      size="medium"
      shape="round"
      icon="chevron-left"
      @click="prevImage"
    />

    <IconButton
      v-if="activeIndex !== files.length - 1"
      class="next-button"
      variant="secondary"
      size="medium"
      shape="round"
      icon="chevron-right"
      @click="nextImage"
    />
  </div>
</template>

<script setup lang="ts">
import { onKeyUp, useSwipe } from '@vueuse/core';
import { computed, ref } from 'vue';

import { IconButton, View, Icon, Text, Link } from '@tallo/design-system';

import { FileDto } from '../interfaces/file.dto';
import { isImage } from '../utils/is-image.util';
import { isPdf } from '../utils/is-pdf.util';
import FileViewerImage from './file-viewer-image.vue';
import FileViewerPdf from './file-viewer-pdf.vue';

const props = defineProps<{ files: FileDto[] }>();
const activeIndex = defineModel<number>('activeIndex', { required: true });
const imagesContainerRef = ref<HTMLElement | null>(null);
const activeFile = computed(() => props.files[activeIndex.value]);

onKeyUp('ArrowLeft', () => {
  prevImage();
});

onKeyUp('ArrowRight', () => {
  nextImage();
});

useSwipe(imagesContainerRef, {
  onSwipeEnd(event, direction) {
    switch (direction) {
      case 'left':
        nextImage();
        break;
      case 'right':
        prevImage();
        break;
    }
  },
});

function nextImage() {
  if (activeIndex.value < props.files.length - 1) {
    activeIndex.value += 1;
  }
}

function prevImage() {
  if (activeIndex.value !== 0) {
    activeIndex.value -= 1;
  }
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.images-wrapper {
  position: relative;

  .file {
    height: 75vh;
  }
}

.prev-button,
.next-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.prev-button {
  left: 2rem;
}

.next-button {
  right: 2rem;
}
</style>
